import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { Textarea } from "@/components/ui/textarea";
import { useDoubleHotkey } from "@/features/reader/notes/hooks/useDoubleHotkey";
import { useVoiceToText } from "@/features/reader/notes/hooks/useVoiceToText";
import { useApi } from "@/hooks/use-api";
import { useAppState, useAppDispatch } from "@/store/useApp";
import { Mic, RotateCcw, Send, Square } from "lucide-react";
import { useCallback, useEffect, useRef, useState } from "react";
import { useHotkeys } from "react-hotkeys-hook";
import { toast } from "sonner";
import { Message } from "./ChatMessage";

interface CopilotChatProps {
  documentId: string;
}

export const CopilotChat = ({ documentId }: CopilotChatProps) => {
  const messages = useAppState((state) => state.chatMessages);
  const selectedBlockId = useAppState((state) => state.selectedBlockId);
  const copilotVisible = useAppState((state) => state.copilotVisible);
  const dispatch = useAppDispatch();
  const [inputMessage, setInputMessage] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const { fetchChatMessages, sendChatMessage, clearChatMessages, transcribeMedia } = useApi();

  // Fetch chat messages when document changes
  useEffect(() => {
    if (messages === null) {
      setIsLoading(true);
      fetchChatMessages(documentId)
        .catch(console.error)
        .finally(() => setIsLoading(false));
    }
  }, [documentId, fetchChatMessages, messages]);

  // Scroll to bottom when messages change
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  // Focus textarea when chat panel becomes visible
  useEffect(() => {
    if (copilotVisible && textareaRef.current) {
      // Small delay to ensure the panel is fully rendered
      requestAnimationFrame(() => {
        textareaRef.current?.focus();
      });
    }
  }, [copilotVisible]);

  // Maintain focus after receiving assistant's response
  useEffect(() => {
    if (!isLoading && messages && messages.length > 0 && copilotVisible) {
      const lastMessage = messages[messages.length - 1];
      // If the last message is from the assistant, refocus the textarea
      if (lastMessage.role === "assistant") {
        requestAnimationFrame(() => {
          textareaRef.current?.focus();
        });
      }
    }
  }, [isLoading, messages, copilotVisible]);

  const handleSendMessage = useCallback(async () => {
    if (!documentId || !inputMessage.trim()) return;

    setInputMessage("");
    setIsLoading(true);

    try {
      await sendChatMessage(documentId, inputMessage, selectedBlockId);
    } finally {
      setIsLoading(false);
      // Maintain focus on the textarea after sending
      textareaRef.current?.focus();
    }
  }, [documentId, inputMessage, selectedBlockId, sendChatMessage]);

  const handleTranscription = useCallback(
    (text: string) => {
      setInputMessage((prev) => {
        // If there's existing text, append with a space, otherwise use the transcribed text
        return prev.trim() ? `${prev} ${text}` : text;
      });
    },
    []
  );

  const {
    isRecording,
    isTranscribing,
    transcriptionError,
    recordingError,
    isRecorderSupported,
    startRecording,
    stopRecording,
    resetVoiceState,
  } = useVoiceToText({
    onTranscription: handleTranscription,
    transcribeMedia,
  });

  const handleStartRecording = useCallback(() => {
    void startRecording();
  }, [startRecording]);

  const handleStopRecording = useCallback(() => {
    stopRecording();
  }, [stopRecording]);

  const toggleRecordingWithShortcut = useCallback(() => {
    if (!isRecorderSupported || isTranscribing || isLoading) {
      return;
    }

    if (isRecording) {
      handleStopRecording();
    } else {
      handleStartRecording();
    }
  }, [
    isRecorderSupported,
    isTranscribing,
    isLoading,
    isRecording,
    handleStartRecording,
    handleStopRecording,
  ]);

  useDoubleHotkey({
    enabled: true,
    onTrigger: toggleRecordingWithShortcut,
  });

  // Handle ESC key to cancel recording
  useHotkeys(
    "esc",
    () => {
      if (isRecording) {
        resetVoiceState();
      }
    },
    {
      enabled: isRecording,
      preventDefault: true,
    },
    [isRecording, resetVoiceState]
  );

  // Handle Enter key to send message (but not Shift+Enter)
  useHotkeys(
    "enter",
    (e) => {
      if (!e.shiftKey && !isLoading && !isRecording && !isTranscribing && inputMessage.trim()) {
        e.preventDefault();
        void handleSendMessage();
      }
    },
    {
      enabled: !isLoading && !isRecording && !isTranscribing,
      enableOnFormTags: true,
      preventDefault: true,
      },
    [isLoading, isRecording, isTranscribing, inputMessage, handleSendMessage]
  );

  // Handle "[" key to close the chat panel
  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
      if (e.key === "[") {
        e.preventDefault();
        dispatch({ type: "TOGGLE_COPILOT" });
      }
    },
    [dispatch]
  );

  // Show error toasts
  useEffect(() => {
    const errorMessage = recordingError || transcriptionError;
    if (errorMessage) {
      toast.error(errorMessage);
    }
  }, [recordingError, transcriptionError]);

  const useMessages = messages || [];

  return (
    <div className="flex flex-col h-full" data-test-role="copilot-chat">
      <div className="flex-1 overflow-y-auto space-y-2 mb-4 pr-4">
        {messages === null && isLoading ? (
          <div className="flex flex-col gap-3">
            <div className="flex flex-col gap-2 w-max max-w-[80%] mr-auto">
              <div className="bg-purple-50 border border-purple-200 rounded-lg p-2">
                <Skeleton className="h-4 w-[220px] mb-2" />
                <Skeleton className="h-4 w-[160px]" />
              </div>
            </div>
            <div className="flex flex-col gap-2 w-max max-w-[80%] ml-auto">
              <div className="bg-gray-50 border border-gray-200 rounded-lg p-2">
                <Skeleton className="h-4 w-[180px]" />
              </div>
            </div>
          </div>
        ) : useMessages.length === 0 ? (
          <div className="flex flex-col items-center justify-center h-[90%] text-center">
            <div className="text-purple-500 font-semibold">
              Chat with Claire
            </div>
            <div className="text-purple-300 text-xs">
              Ask any questions about this document
            </div>
          </div>
        ) : (
          <>
            {useMessages.map((message, index) => (
              <Message
                key={index}
                role={message.role}
                message={message.message}
                createdAt={message.createdAt}
              />
            ))}
            {isLoading && useMessages.length > 0 && useMessages[useMessages.length - 1].role === "user" && (
              <div className="flex flex-col gap-2 w-max max-w-[80%] mr-auto">
                <p className="text-sm rounded-lg px-2 py-2 leading-normal whitespace-pre-wrap wrap-break-word border border-dashed border-purple-300 bg-purple-50 text-purple-600">
                  Typing…
                </p>
              </div>
            )}
          </>
        )}
        <div ref={messagesEndRef} />
      </div>
      <div className="relative">
        <Textarea
          ref={textareaRef}
          value={inputMessage}
          onChange={(e) => setInputMessage(e.target.value)}
          onKeyDown={handleKeyDown}
          placeholder={isRecording ? "Listening..." : "Type a message..."}
          className="pr-20 min-h-[120px] resize-none shadow-xs focus-visible:ring-0 focus-visible:ring-offset-0 border border-purple-300 focus-visible:border-purple-300 text-sm text-purple-500 placeholder:text-purple-300"
          disabled={isLoading || isRecording || isTranscribing}
        />
        <div className="absolute right-2 bottom-2 flex">
          {isRecorderSupported && (
            <Button
              size="icon"
              variant="ghost"
              onClick={isRecording ? handleStopRecording : handleStartRecording}
              disabled={isTranscribing || isLoading}
              className={isRecording ? "text-purple-500" : "text-purple-500"}
            >
              {isRecording ? (
                <Square className="w-4 h-4 text-purple-500" />
              ) : (
                <Mic className="w-4 h-4 text-purple-500" />
              )}
            </Button>
          )}
          <Button
            size="icon"
            variant="ghost"
            aria-label="Send message"
            onClick={handleSendMessage}
            disabled={isLoading || !inputMessage.trim() || isRecording || isTranscribing}
          >
            <Send className="w-4 h-4 text-purple-500" />
          </Button>
        </div>
      </div>
      {useMessages.length > 0 && (
        <div className="mt-2">
          <Button
            variant="link"
            size="sm"
            className="text-purple-700 px-0 hover:no-underline"
            onClick={async () => {
              if (!documentId) return;
              setIsLoading(true);
              try {
                await clearChatMessages(documentId);
              } finally {
                setIsLoading(false);
              }
            }}
            disabled={isLoading}
          >
            Reset chat <RotateCcw className="ml-1 h-4 w-4" />
          </Button>
        </div>
      )}
    </div>
  );
};
